import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemLabel } from 'components/TotemLabel';
import { Badge } from '@/components/ui/badge';
import { useAddClosingDateRangeToSitesMutation, TotadminPermission } from 'data/__generated__';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

type SelectedSite = { _id: string; name: string };

type Props = {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    selectedSites: SelectedSite[];
    onApplied: () => void;
};

export const BatchClosurePopup: React.FC<Props> = ({ isOpen, setIsOpen, selectedSites, onApplied }) => {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [addClosingDateRange, { loading }] = useAddClosingDateRangeToSitesMutation();

    const reset = () => {
        setStartDate(null);
        setEndDate(null);
    };

    const handleClose = (open: boolean) => {
        setIsOpen(open);
        if (!open) reset();
    };

    const onApply = async () => {
        if (!startDate || !endDate) {
            toast.error('Veuillez renseigner les dates de fermeture et de réouverture');
            return;
        }
        if (startDate >= endDate) {
            toast.error('La date de réouverture doit être postérieure à la date de fermeture');
            return;
        }
        if (selectedSites.length === 0) {
            toast.error('Aucun site sélectionné');
            return;
        }

        try {
            const result = await addClosingDateRange({
                variables: {
                    siteIds: selectedSites.map(({ _id }) => _id),
                    range: {
                        start: formatDateAsAnniversary({ dateTime: startDate }),
                        end: formatDateAsAnniversary({ dateTime: endDate }),
                    },
                },
            });
            const payload = result.data?.addClosingDateRangeToSites;
            if (payload?.success === true) {
                toast.success(`Fermeture ajoutée à ${selectedSites.length} site(s)`);
                onApplied();
                handleClose(false);
            } else {
                toast.error(`Erreur lors de l'ajout`);
            }
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Une erreur est survenue lors de la clôture du lot.');
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[560px]">
                <DialogHeader>
                    <DialogTitle className="text-[22px] font-extrabold">Ajouter une période de fermeture</DialogTitle>
                </DialogHeader>
                <Body>
                    <DatesRow>
                        <Field>
                            <TotemLabel>Date de fermeture</TotemLabel>
                            <TotemDatePicker selected={startDate} onChange={(date) => setStartDate(date ?? null)} />
                        </Field>
                        <Field>
                            <TotemLabel>Date de réouverture</TotemLabel>
                            <TotemDatePicker selected={endDate} onChange={(date) => setEndDate(date ?? null)} />
                        </Field>
                    </DatesRow>
                    <SitesSummary>
                        <TotemLabel>Sites concernés ({selectedSites.length})</TotemLabel>
                        <SitesList>
                            {selectedSites.map(({ _id, name }) => (
                                <Badge key={_id} variant="secondary">
                                    {name}
                                </Badge>
                            ))}
                        </SitesList>
                    </SitesSummary>
                    <Actions>
                        <TotemPrimaryButton $isSecondaryStyle type="button" onClick={() => handleClose(false)}>
                            Annuler
                        </TotemPrimaryButton>
                        <TotemPrimaryButton
                            type="button"
                            onClick={onApply}
                            disabled={loading}
                            data-test="batch-closure-apply"
                            requiredPermissions={[TotadminPermission.DeliveryPlanningManage]}
                        >
                            Appliquer
                        </TotemPrimaryButton>
                    </Actions>
                </Body>
            </DialogContent>
        </Dialog>
    );
};

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 0;
`;

const DatesRow = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const SitesSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 200px;
    overflow-y: auto;
`;

const SitesList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;
